import { debounce } from 'lodash';
import { useAppDispatch } from '../../core/hooks/storeHooks';
import { scrollViewportByButtons } from '../redux/sagas/views/viewsActions';
import { DataDirection } from '../../core/types/ui/position';
import { ReturnTypeofSheetSectionsSelector } from '../selectors/sheetUiSelector';
import { useCallback, useEffect } from 'react';
import { Freeze, GridEntity, IRange } from '../types';
import { inRange } from '../../core/utils/general/general';
import { doesRangeInterects, isRangeWithinRange } from '../utils/area/range';

// export type ScrollEntityType = 'col' | 'row';
export type ScrollButtonInteraction = (
  enType: GridEntity,
  direction: DataDirection
) => void;

export interface UseScrollbar {
  startSingleScroll: ScrollButtonInteraction;
  jumpScrollToStartEnd: ScrollButtonInteraction;
  handleWheelScroll: (e: React.WheelEvent) => void;
}

export default (
  sheetId: string,
  windowWidth: number,
  windowHeight: number,
  maxColIndex: number,
  maxRowIndex: number
  // sheetUiProps: ReturnTypeofSheetSectionsSelector
): UseScrollbar => {
  const dispatch = useAppDispatch();
  let mouseIsDown = false;
  let entityType: GridEntity = GridEntity.Row;
  let timeoutId: ReturnType<typeof setTimeout>;
  let initialTime = new Date().getTime();
  let direction: DataDirection = DataDirection.Forward;
  let skipIndexes = 1;
  const maxSkipIndexes = 20;

  // useEffect(() => {}, [sheetUiProps.colViewport]);

  // useEffect(() => {}, [sheetUiProps.rowViewport]);

  const mouseUp = () => {
    mouseIsDown = false;
    document.removeEventListener('mouseup', mouseUp);
    clearTimeout(timeoutId);
  };

  const jumpScrollToStartEnd = (enType: GridEntity, dir: DataDirection) => {
    const maxIndex =
      enType === GridEntity.Column ? maxColIndex : maxRowIndex;

    dispatch(
      scrollViewportByButtons({
        sheetId: sheetId,
        entityType: enType,
        windowWidth: windowWidth,
        windowHeight: windowHeight,
        direction: dir,
        expectedIndex: dir === DataDirection.Backward ? 0 : maxIndex,
      })
    );
  };

  const startSingleScroll = (enType: GridEntity, dir: DataDirection) => {
    mouseIsDown = true;
    entityType = enType;
    direction = dir;
    initialTime = new Date().getTime();
    document.addEventListener('mouseup', mouseUp);
    timeoutId = setTimeout(() => {
      recursiveSingleScroll();
    }, 10);
  };

  const recursiveSingleScroll = debounce(() => {
    if (mouseIsDown) {
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - initialTime;

      skipIndexes = Math.min(
        1 + Math.floor(elapsedTime / 1500),
        maxSkipIndexes
      );

      dispatch(
        scrollViewportByButtons({
          sheetId: sheetId,
          entityType,
          windowWidth: windowWidth,
          windowHeight: windowHeight,
          direction,
          skipIndexes,
        })
      );

      //   recursiveSingleScroll();
      requestAnimationFrame(recursiveSingleScroll);
    }
  }, 40);

  const handleWheelScroll = useCallback(
    (e: React.WheelEvent) => {
      const { deltaX, deltaY } = e;
      let direction: DataDirection = DataDirection.Forward;

      let scrollHorizontally = false;
      let scrollVertically = false;

      const isHorizontalDeltaGreater = Math.abs(deltaX) > Math.abs(deltaY);

      const delta = isHorizontalDeltaGreater ? deltaX : deltaY;
      if (delta > 0) direction = DataDirection.Forward;
      else if (delta < 0) direction = DataDirection.Backward;

      if (isHorizontalDeltaGreater) {
        scrollHorizontally = true;
      } else {
        scrollVertically = true;
      }

      dispatch(
        scrollViewportByButtons({
          sheetId: sheetId,
          windowWidth: windowWidth,
          windowHeight: windowHeight,
          direction,
          scrollHorizontally,
          scrollVertically,
        })
      );
    },
    [sheetId, windowWidth, windowHeight]
  );

  return {
    startSingleScroll,
    jumpScrollToStartEnd,
    handleWheelScroll,
  };
};

export const calculateThumbSize = (
  scrollAreaSize: number,
  minSize: number,
  maxIndex: number,
  maxSize: number
) => (scrollAreaSize - minSize) / (maxIndex / maxSize + 1);

export const getMaxAvailableCountForScroll = (
  maxIndex: number,
  hiddenRanges: IRange[],
  minIndex: number
) => {
  let visibleCount = maxIndex - minIndex;

  hiddenRanges.forEach((range) => {
    if (
      doesRangeInterects(range, {
        start: minIndex,
        end: maxIndex,
      })
    ) {
      const minRange = Math.max(range.start, minIndex);
      const rangeCount = range.end - minRange + 1;

      visibleCount -= rangeCount;
    }
  });

  return visibleCount;
};

export const mapScrolledIndexToVisibleIndex = (
  scrolledIndex: number,
  hiddenRanges: IRange[],
  minIndex: number
) => {
  let visibleCount = scrolledIndex + minIndex;

  hiddenRanges.forEach((range) => {
    if (
      doesRangeInterects(range, {
        start: minIndex,
        end: visibleCount,
      })
    ) {
      const start = Math.max(range.start, minIndex);
      const rangeCount = range.end - start + 1;
      visibleCount += rangeCount;
    }
  });

  return visibleCount;
};
